import { z } from 'zod';

// Unified password policy across /auth, /reset-password, and AuthGuard.
// Minimum 8 chars; allows long passphrases; trims whitespace on email.
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 100;

export const emailSchema = z
  .string()
  .trim()
  .email({ message: 'Invalid email address' })
  .max(255, { message: 'Email must be less than 255 characters' });

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN, { message: `Password must be at least ${PASSWORD_MIN} characters` })
  .max(PASSWORD_MAX, { message: `Password must be less than ${PASSWORD_MAX} characters` });

export const authCredentialsSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

/**
 * Lightweight password strength scoring (0–4). Pure heuristic — does NOT replace
 * leaked-password (HIBP) checks. Used for inline UX feedback only.
 */
export type PasswordStrength = {
  score: 0 | 1 | 2 | 3 | 4;
  label: 'Too short' | 'Weak' | 'Fair' | 'Good' | 'Strong';
};

export function scorePassword(pw: string): PasswordStrength {
  if (!pw || pw.length < PASSWORD_MIN) return { score: 0, label: 'Too short' };
  let s = 0;
  if (pw.length >= 10) s++;
  if (pw.length >= 14) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const score = Math.max(1, Math.min(4, s)) as 1 | 2 | 3 | 4;
  const labels: Record<1 | 2 | 3 | 4, PasswordStrength['label']> = {
    1: 'Weak',
    2: 'Fair',
    3: 'Good',
    4: 'Strong',
  };
  return { score, label: labels[score] };
}

/**
 * Validate a post-login `?redirect=` param. Only same-origin absolute paths are
 * allowed; reject protocol-relative (`//evil.com`) and absolute URLs.
 */
export function safeInternalRedirect(input: string | null | undefined, fallback = '/chat'): string {
  if (!input) return fallback;
  if (!input.startsWith('/')) return fallback;
  if (input.startsWith('//')) return fallback; // protocol-relative
  if (input.startsWith('/\\')) return fallback;
  return input;
}
